import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BillAmend, BillBackgroundFile, BillRef, BillTimelineStep } from '../types/types';
import { buttonReset, color, font, radius, space, tone as tones, weight } from '../theme';
import { canPlayInApp } from '../utils/live';
import { countLabel } from '../utils/format';
import { Card, CardHeader, Section } from './Card';
import { Badge, StageBadge } from './Badge';
import ListRow from './ListRow';
import Icon from './Icon';
import PlayerDialog from './PlayerDialog';

/* The bill page's parts built from the Knesset website's bill page (--bill-timeline):
   the dated progress steps, the timeline grouped by stage (decision 2A), the "מתקן את"
   line, the merge and split links ("2 · מצב") and the background files. Bills without
   these fields (preliminary bills, or ones not fetched yet) never render them. */

/* ── Links and formatting ─────────────────────────────────────────────── */

// The bill's and a law's pages on the Knesset site
export const knessetBillUrl = (billDocId: string): string | null => {
  const m = /^bill_(\d+)$/.exec(billDocId);
  return m ? `https://main.knesset.gov.il/apps/legislation/main/bills/${m[1]}` : null;
};
export const knessetLawUrl = (israelLawId: number): string =>
  `https://main.knesset.gov.il/apps/legislation/main/laws/${israelLawId}`;

// 'YYYY-MM-DD…' (Israel-local) → '3.3.2025', or '3.3.25' when short; read from the string
// itself, so no time zone can shift the day
export const formatDay = (iso: string | null | undefined, short = false): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return '';
  return `${Number(m[3])}.${Number(m[2])}.${short ? m[1].slice(2) : m[1]}`;
};

// Another bill's name as the Knesset gives it, "הצעת חוק … (פ/5933/25) (כ/1180)": the
// trailing numbers move to a meta line (the first one is the bill's own), and a trailing
// "[פוצלה לפי סעיף 84(ב) לתקנון]" note is dropped (the card already says it was split)
export const splitBillName = (name: string): { title: string; number: string | null } => {
  let title = (name || '').trim();
  const numbers: string[] = [];
  let changed = true;
  while (changed) {
    changed = false;
    const code = /\s*\(([א-ת]{1,2}\/[\d/]+)\)\s*$/.exec(title);
    if (code) { numbers.unshift(code[1]); title = title.slice(0, code.index).trim(); changed = true; continue; }
    const note = /\s*\[[^\]]*פוצל[^\]]*\]\s*$/.exec(title);
    if (note) { title = title.slice(0, note.index).trim(); changed = true; }
  }
  return { title: title || name, number: numbers.length > 0 ? numbers[0] : null };
};

// 'מס\' 47' → 'תיקון מס\' 47' (already-labelled numbers stay as they are), kept on one line
const correctionLabel = (n: string): string => (/^תיקון/.test(n) ? n : `תיקון ${n}`).replace(/ /g, String.fromCharCode(160));

// "in <place>": ב + the name, with the Hebrew spelling rules for ו and ה at its start
// (ועדת הכספים → בוועדת הכספים, הוועדה המשותפת → בוועדה המשותפת)
export const inPlace = (place: string): string => {
  if (place.indexOf('במליאה') === 0) return place;
  if (/^ה/.test(place)) return `ב${place.slice(1)}`;
  if (/^ו[^ו]/.test(place)) return `בו${place}`;
  return `ב${place}`;
};

const placeLabel = (row: BillTimelineStep): string =>
  row.origin === 'plenum' || row.place === 'במליאה' ? 'מליאה' : (row.place || 'ועדה');

/* ── Local icons (not in the shared Icon set) ─────────────────────────── */

const LOCAL_ICONS = {
  scale: (<><path d="M12 4v16M7 20h10M5 8h14" /><path d="M5 8l-2.5 6a2.8 2.8 0 0 0 5 0z" /><path d="M19 8l-2.5 6a2.8 2.8 0 0 0 5 0z" /></>),
  merge: (<><circle cx="6" cy="5" r="2" /><circle cx="6" cy="19" r="2" /><circle cx="18" cy="12" r="2" /><path d="M6 7v10M6 9c0 3 3 3 6 3h4" /></>),
};

const LocalIcon = ({ name, size = 20, strokeWidth = 1.8, style }: { name: keyof typeof LOCAL_ICONS; size?: number; strokeWidth?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, display: 'block', ...style }}>
    {LOCAL_ICONS[name]}
  </svg>
);

const linkStyle: React.CSSProperties = { color: color.primary, fontWeight: weight.medium, textDecoration: 'none' };

/* ── Dated progress steps ─────────────────────────────────────────────── */

export interface ProgressStep { key: string; label: string; date?: string }

// The six stages with the day the bill reached each one under its dot; dots up to the
// current stage are filled
export const DatedProgress = ({ steps, current }: { steps: ProgressStep[]; current: number }) => {
  const n = steps.length;
  const edge = `${50 / n}%`;
  const span = 100 - 100 / n;
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, position: 'relative' }}>
      <span aria-hidden="true" style={{ position: 'absolute', top: 5, right: edge, left: edge, height: 2, background: color.track }} />
      <span aria-hidden="true" style={{ position: 'absolute', top: 5, right: edge, width: `${(span * Math.max(0, current)) / (n - 1)}%`, height: 2, background: color.primary }} />
      {steps.map((s, i) => {
        const reached = i <= current;
        const date = reached ? formatDay(s.date, true) : '';
        return (
          <li key={s.key} aria-current={i === current ? 'step' : undefined} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.xs, textAlign: 'center', minWidth: 0 }}>
            <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, background: reached ? color.primary : color.track, boxShadow: i === current ? `0 0 0 4px ${color.primarySoft}` : 'none' }} />
            <span style={{ fontSize: font.caption, lineHeight: 1.25, fontWeight: weight.medium, color: reached ? color.text : color.textTertiary }}>{s.label}</span>
            {date && <span style={{ fontSize: font.caption, color: color.textTertiary, fontVariantNumeric: 'tabular-nums' }}>{date}</span>}
          </li>
        );
      })}
    </ol>
  );
};

/* ── The timeline, grouped by stage (2A) ──────────────────────────────── */

type StageKey = 'preliminary' | 'prepFirst' | 'first' | 'prepSecond' | 'secondThird' | 'other';
const STAGE_LABEL: Record<StageKey, string> = {
  preliminary: 'דיון מוקדם',
  prepFirst: 'הכנה לקריאה ראשונה',
  first: 'קריאה ראשונה',
  prepSecond: 'הכנה לקריאה שנייה ושלישית',
  secondThird: 'קריאה שנייה ושלישית',
  other: 'דיונים בכנסת',
};
const SECOND = /שנייה|שניה/;

// The stage a step belongs to, from its title; null for procedural steps (קביעת ועדה
// מטפלת, אישור מיזוג, בקשה להקדמת הדיון…), which join the stage before them
const stageOf = (row: BillTimelineStep): StageKey | null => {
  const t = row.title || '';
  const plenum = row.origin === 'plenum';
  if (t.indexOf('מוקדם') !== -1) return 'preliminary';
  if (t.indexOf('ראשונה') !== -1) return plenum ? 'first' : 'prepFirst';
  if (SECOND.test(t) || t.indexOf('שלישית') !== -1) return plenum ? 'secondThird' : 'prepSecond';
  return null;
};

// Short labels for the Knesset's step titles (the stage and place are in the group heading)
const EVENT_LABELS: Record<string, string> = {
  'במליאה לדיון מוקדם': 'דיון מוקדם במליאה',
  'לדיון במליאה לקראת הקריאה הראשונה': 'דיון במליאה לקראת הקריאה הראשונה',
  'לדיון במליאה לקראת קריאה שנייה-שלישית': 'דיון לקראת קריאה שנייה ושלישית',
  'לדיון במליאה לקראת קריאה שלישית': 'קריאה שלישית',
};
const eventLabel = (row: BillTimelineStep): string => {
  const t = row.title || '';
  if (EVENT_LABELS[t]) return EVENT_LABELS[t];
  if (t.indexOf('הונחה על שולחן הכנסת') === 0) return 'הונחה על שולחן הכנסת';
  if (row.origin === 'committee' && t.indexOf('הכנה לקריאה') === 0) return 'ישיבת ועדה';
  if (t.indexOf('לאישור ') === 0) return t.slice(1);
  return t || (row.origin === 'plenum' ? 'ישיבת מליאה' : 'ישיבת ועדה');
};

interface StageGroup { key: StageKey; rows: BillTimelineStep[] }

// Consecutive steps of one stage form a group; procedural steps join the group before them
// (or the first group, when they come first)
const groupByStage = (rows: BillTimelineStep[]): StageGroup[] => {
  const groups: StageGroup[] = [];
  let pending: BillTimelineStep[] = [];
  for (const row of rows) {
    const key = stageOf(row);
    const last = groups.length > 0 ? groups[groups.length - 1] : null;
    if (key === null) {
      if (last) last.rows.push(row);
      else pending.push(row);
    } else if (last && last.key === key) {
      last.rows.push(row);
    } else {
      groups.push({ key, rows: pending.concat([row]) });
      pending = [];
    }
  }
  if (pending.length > 0) groups.push({ key: 'other', rows: pending });
  return groups;
};

// The merged card's caption. Where: the committee that last prepared this bill (its own
// timeline), else its handling committee. When: the merge approval step ("לאישור מיזוג",
// usually in ועדת הכנסת), from the leading bill's timeline or else its own.
export const mergeCaption = (own: BillTimelineStep[], leader: BillTimelineStep[] | undefined, fallbackCommittee: string): string => {
  let where = '';
  for (const r of own) if (r.origin === 'committee' && stageOf(r) !== null && r.place) where = r.place;
  if (!where) where = fallbackCommittee;
  let approval: BillTimelineStep | null = null;
  for (const rows of [leader || [], own]) {
    for (const r of rows) if ((r.title || '').indexOf('מיזוג') !== -1 && r.date) approval = r;
    if (approval) break;
  }
  const approvedIn = approval ? placeLabel(approval) : '';
  const approvedOn = approval ? `ב-${formatDay(approval.date)}` : '';
  if (where && approval && approvedIn === where) return `מוזגה ${inPlace(where)} ${approvedOn}`;
  if (where && approval) return `מוזגה ${inPlace(where)}, אושר ${inPlace(approvedIn)} ${approvedOn}`;
  if (where) return `מוזגה ${inPlace(where)}`;
  if (approval) return `המיזוג אושר ${inPlace(approvedIn)} ${approvedOn}`;
  return '';
};

const uniquePlaces = (rows: BillTimelineStep[]): string[] => {
  const out: string[] = [];
  for (const r of rows) {
    const p = placeLabel(r);
    if (out.indexOf(p) === -1) out.push(p);
  }
  return out;
};

const ACTION: React.CSSProperties = {
  ...buttonReset, width: 36, height: 36, borderRadius: radius.md, flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: color.primarySoft, color: color.primary, textDecoration: 'none',
};
const ACTION_OFF: React.CSSProperties = { ...ACTION, background: 'transparent', color: color.textSecondary, opacity: 0.35, cursor: 'default' };
const INLINE_ICON: React.CSSProperties = { display: 'inline-block', verticalAlign: -2 };

// Long stages (a budget bill's dozens of committee sittings) show their first and last
// steps until opened
const COLLAPSE_OVER = 6;
const COLLAPSED_EDGE = 2;

interface BillTimelineProps {
  billId: string;                // bill_{id}
  billTitle: string;             // the player's "על סדר היום"
  timeline: BillTimelineStep[];
  timelineTotal?: number;
  passed: boolean;               // the last reading gets "התקבלה" and a green dot
  meta?: string;                 // replaces the step count in the heading ('עד המיזוג')
}

export const BillTimeline = ({ billId, billTitle, timeline, timelineTotal, passed, meta }: BillTimelineProps) => {
  const [playing, setPlaying] = useState<BillTimelineStep | null>(null);
  const [opened, setOpened] = useState<Record<number, boolean>>({});
  const billUrl = knessetBillUrl(billId);
  const groups = groupByStage(timeline);
  const total = Math.max(timelineTotal || 0, timeline.length);
  const hidden = total - timeline.length;

  // The last third-reading step of a passed bill says so
  let passedIndex = -1;
  if (passed) {
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].origin === 'plenum' && eventLabel(timeline[i]) === 'קריאה שלישית') { passedIndex = i; break; }
    }
  }
  const hasInApp = timeline.some(r => canPlayInApp(r.videoUrl));
  const hasOutside = timeline.some(r => !!r.videoUrl && !canPlayInApp(r.videoUrl));

  const renderRow = (row: BillTimelineStep, first: boolean, showPlace: boolean) => {
    const day = formatDay(row.date);
    const index = timeline.indexOf(row);
    const label = eventLabel(row) + (index === passedIndex ? ' · התקבלה' : '');
    const inApp = canPlayInApp(row.videoUrl);
    return (
      <li key={index} style={{ display: 'flex', alignItems: 'center', gap: space.sm + 2, padding: `${space.sm}px 0`, borderTop: first ? 'none' : `1px solid ${color.divider}` }}>
        <span style={{ width: 74, flexShrink: 0, fontSize: font.small, fontWeight: weight.bold, color: color.text, fontVariantNumeric: 'tabular-nums' }}>{day || '—'}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: font.label, lineHeight: 1.35 }}>
          {label}
          {showPlace && <span style={{ display: 'block', fontSize: font.caption, color: color.textTertiary }}>{placeLabel(row)}</span>}
        </span>
        <span style={{ display: 'flex', gap: space.xs + 2, flexShrink: 0 }}>
          {row.protocolUrl
            ? <a href={row.protocolUrl} target="_blank" rel="noreferrer" title="פרוטוקול" aria-label={`פרוטוקול, ${label}${day ? `, ${day}` : ''}`} style={ACTION}><Icon name="document" size={18} strokeWidth={2} /></a>
            : <span title="אין פרוטוקול" aria-hidden="true" style={ACTION_OFF}><Icon name="document" size={18} strokeWidth={2} /></span>}
          {inApp
            ? <button type="button" onClick={() => setPlaying(row)} title="צפייה בהקלטה" aria-label={`צפייה בהקלטה, ${label}${day ? `, ${day}` : ''}`} style={ACTION}><Icon name="play" size={18} /></button>
            : row.videoUrl && billUrl
              ? <a href={billUrl} target="_blank" rel="noreferrer" title="צפייה בהקלטה באתר הכנסת" aria-label={`צפייה בהקלטה באתר הכנסת, ${label}${day ? `, ${day}` : ''}`} style={ACTION}><Icon name="play" size={18} /></a>
              : <span title="אין הקלטה" aria-hidden="true" style={ACTION_OFF}><Icon name="play" size={18} /></span>}
        </span>
      </li>
    );
  };

  return (
    <Card>
      <CardHeader title="מסלול החקיקה" meta={meta || (hidden > 0 ? `${timeline.length} מתוך ${total} שלבים` : countLabel(timeline.length, 'שלב אחד', 'שלבים'))} />
      <ol style={{ listStyle: 'none', margin: 0, padding: `0 22px 0 0`, position: 'relative' }}>
        <span aria-hidden="true" style={{ position: 'absolute', right: 6, top: 8, bottom: 8, width: 2, borderRadius: 1, background: color.track }} />
        {groups.map((g, gi) => {
          const places = uniquePlaces(g.rows);
          const done = passed && g.key === 'secondThird' && gi === groups.length - 1;
          const collapsed = g.rows.length > COLLAPSE_OVER && !opened[gi];
          const head = collapsed ? g.rows.slice(0, COLLAPSED_EDGE) : g.rows;
          const tail = collapsed ? g.rows.slice(g.rows.length - COLLAPSED_EDGE) : [];
          return (
            <li key={gi} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: space.xxs, paddingBottom: gi === groups.length - 1 ? space.xs : 14 }}>
              <span aria-hidden="true" style={{ position: 'absolute', right: -22, top: 3, width: 14, height: 14, borderRadius: '50%', background: done ? tones.success.fg : color.primary, boxShadow: `0 0 0 3px ${color.surface}` }} />
              <h3 style={{ margin: 0, fontSize: font.body, fontWeight: weight.bold, color: color.text }}>{STAGE_LABEL[g.key]}</h3>
              <span style={{ fontSize: font.small, color: color.textSecondary }}>{places.join(' · ')}</span>
              <ul style={{ listStyle: 'none', margin: `${space.xs}px 0 0`, padding: 0 }}>
                {head.map((r, i) => renderRow(r, i === 0, places.length > 1))}
                {collapsed && (
                  <li style={{ borderTop: `1px solid ${color.divider}` }}>
                    <button type="button" onClick={() => setOpened(o => ({ ...o, [gi]: true }))} style={{ ...buttonReset, minHeight: 44, width: '100%', display: 'flex', alignItems: 'center', gap: space.xs, fontSize: font.small, fontWeight: weight.medium, color: color.primary }}>
                      <Icon name="chevronDown" size={16} strokeWidth={2.2} />
                      הצגת {g.rows.length - COLLAPSED_EDGE * 2} שלבים נוספים
                    </button>
                  </li>
                )}
                {tail.map(r => renderRow(r, false, places.length > 1))}
              </ul>
            </li>
          );
        })}
      </ol>
      {/* What the two buttons open. Plenum recordings can't play here (utils/live canPlayInApp),
          so they link to the bill's page on the Knesset site. */}
      {(hasInApp || hasOutside || timeline.some(r => !!r.protocolUrl)) && (
        <p style={{ margin: `${space.sm}px 0 0`, fontSize: font.caption, lineHeight: 1.5, color: color.textTertiary }}>
          <Icon name="document" size={13} style={INLINE_ICON} /> פרוטוקול נפתח כקובץ באתר הכנסת
          {(hasInApp || hasOutside) && <> · <Icon name="play" size={12} style={INLINE_ICON} /> </>}
          {hasInApp && !hasOutside && 'הקלטה נפתחת בנגן של האתר'}
          {hasInApp && hasOutside && 'הקלטת ועדה נפתחת בנגן של האתר, הקלטת מליאה באתר הכנסת'}
          {!hasInApp && hasOutside && 'הקלטה נפתחת באתר הכנסת'}
        </p>
      )}
      {hidden > 0 && billUrl && (
        <a href={billUrl} target="_blank" rel="noreferrer" style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: space.xs, minHeight: 36, marginTop: space.xs, fontSize: font.small }}>
          {countLabel(hidden, 'שלב מוקדם נוסף', 'שלבים מוקדמים נוספים')} באתר הכנסת
          <Icon name="external" size={14} />
        </a>
      )}
      {playing && playing.videoUrl && (
        <PlayerDialog
          open
          onClose={() => setPlaying(null)}
          src={playing.videoUrl}
          title={placeLabel(playing) === 'מליאה' ? 'ישיבת המליאה' : placeLabel(playing)}
          subtitle={[formatDay(playing.date), eventLabel(playing)].filter(Boolean).join(' · ')}
          agenda={billTitle}
          externalUrl={billUrl}
          externalLabel="הצעת החוק באתר הכנסת"
        />
      )}
    </Card>
  );
};

/* ── "מתקן את" ────────────────────────────────────────────────────────── */

const LawName = ({ law }: { law: BillAmend }) => (law.israelLawId
  ? <a href={knessetLawUrl(law.israelLawId)} target="_blank" rel="noreferrer" style={linkStyle}>{law.name}</a>
  : <span style={{ color: color.text, fontWeight: weight.medium }}>{law.name}</span>);

// Under the bill's title: the law it amends, linking out to the law's page on the Knesset
// site; further laws open with "ועוד N"
export const AmendsLine = ({ amends }: { amends: BillAmend[] }) => {
  const [open, setOpen] = useState(false);
  if (amends.length === 0) return null;
  const shown = open ? amends : amends.slice(0, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: space.xs + 2, fontSize: font.small, color: color.textSecondary, lineHeight: 1.45 }}>
      <LocalIcon name="scale" size={16} style={{ marginTop: 2 }} />
      <span style={{ minWidth: 0 }}>
        {shown.map((law, i) => (
          <span key={`${law.israelLawId}-${law.name}`} style={{ display: i === 0 ? 'inline' : 'block' }}>
            {i === 0 && 'מתקן את '}
            <LawName law={law} />
            {law.correctionNumber && ` · ${correctionLabel(law.correctionNumber)}`}
          </span>
        ))}
        {!open && amends.length > 1 && (
          <>
            {' · '}
            <button type="button" onClick={() => setOpen(true)} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium }}>
              ועוד {countLabel(amends.length - 1, 'חוק אחד', 'חוקים')}
            </button>
          </>
        )}
      </span>
    </div>
  );
};

// The facts row's value (desktop side panel): every amended law, one per line
export const AmendsFact = ({ amends }: { amends: BillAmend[] }) => (
  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: space.xxs }}>
    {amends.map(law => <LawName key={`${law.israelLawId}-${law.name}`} law={law} />)}
  </span>
);

/* ── Merges and splits ("2 · מצב") ────────────────────────────────────── */

type NavFrom = { from: string };

// One linked bill in a list card; a bill we have no doc for shows its name only
const BillRefRow = ({ refBill, first, from, badge }: { refBill: BillRef; first: boolean; from: string; badge?: string }) => {
  const { title, number } = splitBillName(refBill.name);
  const sub = badge || number
    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs + 2, marginTop: space.xxs }}>{badge && <Badge>{badge}</Badge>}{number}</span>
    : undefined;
  return (
    <ListRow
      divider={!first}
      to={refBill.billDocId ? `/legislation/${refBill.billDocId}` : undefined}
      state={refBill.billDocId ? ({ from } as NavFrom) : undefined}
      title={<span style={{ fontSize: font.label }}>{title}</span>}
      subtitle={sub}
    />
  );
};

// A card of linked bills: "הצעות שמוזגו לתוכה", "פוצלה מתוך", "הצעות שפוצלו ממנה"
export const BillRefsCard = ({ title, refs, from, badge, showCount = true }: { title: string; refs: BillRef[]; from: string; badge?: string; showCount?: boolean }) => (
  <Card padding={0}>
    <div style={{ padding: `${space.lg}px ${space.lg}px 0` }}>
      <CardHeader title={title} meta={showCount ? refs.length : undefined} style={{ marginBottom: space.xs }} />
    </div>
    {refs.map((r, i) => <BillRefRow key={`${r.billDocId || ''}-${r.name}`} refBill={r} first={i === 0} from={from} badge={badge} />)}
  </Card>
);

// The same list on the page background (a merged bill's "מוזגו איתה")
export const BillRefsSection = ({ title, refs, from }: { title: string; refs: BillRef[]; from: string }) => (
  <Section title={title} meta={refs.length}>
    <Card padding={0}>
      {refs.map((r, i) => <BillRefRow key={`${r.billDocId || ''}-${r.name}`} refBill={r} first={i === 0} from={from} />)}
    </Card>
  </Section>
);

// Instead of the red "מוזגה" card: the bill it was merged into, with that bill's stage
export const MergedIntoCard = ({ leader, leaderStage, caption, from }: { leader: BillRef; leaderStage?: string; caption?: string; from: string }) => {
  const { title, number } = splitBillName(leader.name);
  const inner = (
    <>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <span style={{ fontSize: font.body, fontWeight: weight.medium, lineHeight: 1.35, color: color.text }}>{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, flexWrap: 'wrap' }}>
          {leaderStage && <StageBadge stage={leaderStage} />}
          <span style={{ fontSize: font.small, color: color.textSecondary }}>{['ההצעה המובילה', number].filter(Boolean).join(' · ')}</span>
        </span>
      </span>
      {leader.billDocId && <span style={{ color: color.textTertiary }}><Icon name="chevronForward" size={18} /></span>}
    </>
  );
  const innerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: space.sm + 2, padding: `${space.md}px ${space.md + 2}px`,
    background: color.surface, borderRadius: radius.lg, textDecoration: 'none', color: color.text,
  };
  return (
    <Card style={{ background: color.primarySoft, boxShadow: 'none' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm + 2 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, fontSize: font.label, fontWeight: weight.bold, color: color.primaryInk }}>
          <LocalIcon name="merge" size={18} strokeWidth={2} />
          ההצעה מוזגה לתוך הצעה אחרת
        </span>
        {leader.billDocId
          ? <Link to={`/legislation/${leader.billDocId}`} state={{ from } as NavFrom} style={innerStyle}>{inner}</Link>
          : <div style={innerStyle}>{inner}</div>}
        {caption && <span style={{ fontSize: font.small, color: color.primaryInk }}>{caption}</span>}
      </div>
    </Card>
  );
};

/* ── Background files ("חומרי רקע") ───────────────────────────────────── */

const fileType = (url: string): string => {
  const m = /\.([a-z0-9]{2,4})(?:\?|$)/i.exec(url);
  return m ? m[1].toUpperCase() : '';
};
const BACKGROUND_SHOWN = 5;

export const BackgroundFiles = ({ files }: { files: BillBackgroundFile[] }) => {
  const [all, setAll] = useState(false);
  if (files.length === 0) return null;
  const shown = all ? files : files.slice(0, BACKGROUND_SHOWN);
  return (
    <Section title="חומרי רקע" meta={files.length}>
      <Card padding={0}>
        {shown.map((f, i) => (
          <ListRow
            key={f.url}
            divider={i > 0}
            href={f.url}
            leading={<span style={{ color: color.primary }}><Icon name="document" size={22} /></span>}
            title={f.title}
            subtitle={[fileType(f.url), formatDay(f.date)].filter(Boolean).join(' · ')}
          />
        ))}
        {!all && files.length > BACKGROUND_SHOWN && (
          <button type="button" onClick={() => setAll(true)} style={{ ...buttonReset, width: '100%', minHeight: 48, borderTop: `1px solid ${color.divider}`, fontSize: font.label, fontWeight: weight.medium, color: color.primary }}>
            הצגת כל {files.length} הקבצים
          </button>
        )}
      </Card>
    </Section>
  );
};
